/**
 * Product-agnostic SolShare software update decisions.
 * Uses solshareVersion (+ opaque component status for errors / in-progress).
 * Never branches on MK1/MK2 or component slug names for the idle-loop.
 * Pre-v1 OTA cutoff is the sole exception (checks fw/app reported).
 */
import semver from "semver";

const ERROR_STATES = new Set(["ERROR", "error", "FAILED", "failed"]);
const TERMINAL_OK_STATES = new Set(["DONE", "done", "SUCCESS", "success"]);
const OTA_CAPABLE_FLOOR = "1.0.0";

export function emptySoftwareState() {
  return {
    components: [],
    solshareVersion: {
      reported: null,
      target: null,
      desired: null,
      minimum: null,
    },
  };
}

export function normalizeSoftwareState(raw) {
  const data = raw ?? {};
  return {
    components: Array.isArray(data.components) ? data.components : [],
    solshareVersion: {
      reported: data.solshareVersion?.reported ?? null,
      target: data.solshareVersion?.target ?? null,
      desired: data.solshareVersion?.desired ?? null,
      minimum: data.solshareVersion?.minimum ?? null,
    },
  };
}

function componentStatusState(component) {
  const state = component?.status?.state;
  return typeof state === "string" && state.length > 0 ? state : null;
}

export function hasComponentError(components) {
  return (components || []).some((component) => {
    const state = componentStatusState(component);
    return state !== null && ERROR_STATES.has(state);
  });
}

export function hasInProgressComponent(components) {
  return (components || []).some((component) => {
    const state = componentStatusState(component);
    if (state === null) {
      return false;
    }
    if (ERROR_STATES.has(state) || TERMINAL_OK_STATES.has(state)) {
      return false;
    }
    return true;
  });
}

export function isHopInProgress(state) {
  const { reported, target } = state.solshareVersion;
  const targetSet = typeof target === "string" && target.length > 0;
  if (targetSet && reported !== target) {
    return true;
  }
  return hasInProgressComponent(state.components);
}

/**
 * Decide the next idle-loop action for one GET snapshot.
 * `intended` is usually solshareVersion.desired.
 */
export function decideSoftwareUpdateAction(state, intended) {
  if (!intended || typeof intended !== "string") {
    return { type: "fail", message: "No intended SolShare software version" };
  }

  if (hasComponentError(state.components)) {
    return { type: "fail", message: "Software update failed on a component" };
  }

  const { reported } = state.solshareVersion;
  if (reported === intended) {
    return { type: "done" };
  }

  if (isHopInProgress(state)) {
    return { type: "poll" };
  }

  return { type: "post", version: intended };
}

function coerceVersion(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }
  const coerced = semver.coerce(value.trim());
  return coerced ? coerced.version : null;
}

/**
 * Compare product SBOM versions (e.g. v1.2.0 vs v3.2.3).
 * Returns true when reported is parseable and >= minimum.
 */
export function isReportedAtOrAboveMinimum(reported, minimum) {
  const min = coerceVersion(minimum);
  if (!min) {
    return true;
  }
  const current = coerceVersion(reported);
  if (!current) {
    return false;
  }
  return semver.gte(current, min);
}

/**
 * Derive commissioning need/force from v3 GET solshareVersion only.
 * No fallback to legacy component-min flags.
 *
 * - needSoftwareUpdate: desired is set and reported !== desired
 * - forceUpdate: minimum is set and reported is null or < minimum
 */
export function deriveSoftwareUpdateRequirement(state) {
  const { reported, desired, minimum } = state.solshareVersion;
  const desiredVersion = typeof desired === "string" ? desired.trim() : "";
  const needSoftwareUpdate =
    Boolean(desiredVersion) && reported !== desiredVersion;
  const forceUpdate = !isReportedAtOrAboveMinimum(reported, minimum);

  return { needSoftwareUpdate, forceUpdate };
}

/**
 * True when a control-app / control-firmware reported version is OTA-capable
 * (legacy component scheme >= v1.0.0). OS_APP firmware encoding counts as capable.
 */
export function isComponentVersionOtaCapable(reported) {
  if (typeof reported !== "string" || !reported.trim()) {
    return false;
  }
  const value = reported.trim();
  if (/^OSv.*_APPv.*$/i.test(value)) {
    return true;
  }
  const coerced = semver.coerce(value);
  if (!coerced) {
    return false;
  }
  return semver.gte(coerced, OTA_CAPABLE_FLOOR);
}

/**
 * Pre-v1 cutoff: both fw and app must be present and OTA-capable.
 * Missing slug or missing/below-v1 reported → not supported for v3 OTA.
 */
export function isDeviceOtaCapable(state) {
  const bySlug = new Map(
    (state.components || []).map((component) => [component.slug, component])
  );
  const fw = bySlug.get("fw");
  const app = bySlug.get("app");
  if (!fw || !app) {
    return false;
  }
  return (
    isComponentVersionOtaCapable(fw.reported) &&
    isComponentVersionOtaCapable(app.reported)
  );
}
